import { AxiosResponse } from 'axios';
import _ from 'lodash';
import { call, put, select, takeEvery, takeLatest, SagaReturnType } from 'redux-saga/effects';

import {
  bulkEmailsVerification,
  createBulkOrder,
  createOrder,
  getLinearGraph,
  getOrderSummary,
  uploadBulkOrder,
} from 'features/dashboard/api';
import { LinearGraphReducerState } from 'features/dashboard/redux/createLinearChartReducer';
import { LinearChartItem } from 'features/dashboard/ts/LinearCharItem';
import { chartModeToStep } from 'features/dashboard/utils/chartModeToStep';
import { addErrorMessage, addSuccessMessage } from 'features/message/redux/action';
import type { LinearGraphAction } from './actions';
import * as actions from './actions';
import * as types from './types';
import { StoreState } from 'store';
import { ErrorUtils } from 'utils';
import { pickLinearGraph, pickRadialChart } from 'features/dashboard/utils/pickGraph';
import { getRangeStart } from 'features/dashboard/utils/getRangeStart';
import { getRangeEnd } from 'features/dashboard/utils/getRangeEnd';
import { RadialChartItem } from 'features/dashboard/ts/RadialChartItem';
import { t } from 'locales/i18n';
import { SendBulkOrder } from '../ts/SendBulkOrder';
import { queryClient } from 'api/query';

function* _getGraphState(action: LinearGraphAction) {
  if (!action.reducerType) return null;

  const state: StoreState = yield select();
  return state.dashboard[action.reducerType];
}

function* getLinearGraphItemsSaga(action: ReturnType<typeof actions.requestLinearGraphData>) {
  try {
    const state: LinearGraphReducerState = yield _getGraphState(action as LinearGraphAction);
    const graphData = pickLinearGraph(state);
    const { rangeAnchor, mode } = state;

    if (graphData.get(rangeAnchor.getTime())) {
      return;
    }

    const step = chartModeToStep(mode);
    const rangeStart = getRangeStart(rangeAnchor, mode);

    yield put(actions.setLinearGraphLoading(action.reducerType, true));

    const apiUrl = (action as LinearGraphAction).apiUrl;
    if (!apiUrl) return;
    const { data } = (yield call(
      getLinearGraph,
      {
        step,
        created_at_before: getRangeEnd(rangeAnchor, mode),
        created_at_after: rangeStart,
      },
      apiUrl
    )) as AxiosResponse<LinearChartItem[]>;

    yield put(actions.setLinearGraphData(action.reducerType, data));
  } catch (error) {
    const message = ErrorUtils.convertError(error);
    yield put(addErrorMessage(message));
  }

  yield put(actions.setLinearGraphLoading(action.reducerType, false));
}

function* getOrderSummarySaga() {
  const store: StoreState = yield select();
  const state = store.dashboard.summaryReducer;
  const graphData = pickRadialChart(state);
  const { rangeStart, mode } = state;

  if (graphData.get(rangeStart.getTime())) {
    return;
  }

  yield put(actions.setSummaryGraphLoading(true));

  const rangeEnd = getRangeEnd(rangeStart, mode, true);
  const step = chartModeToStep(mode);

  const { data } = (yield call(getOrderSummary, {
    step,
    created_at_before: rangeEnd,
    created_at_after: rangeStart,
  })) as AxiosResponse<RadialChartItem[]>;

  yield put(actions.setSummaryGraphData(data));

  yield put(actions.setSummaryGraphLoading(false));
}

function* createOrderSaga(action: ReturnType<typeof actions.createOrderRequest>) {
  const data = action.payload;

  try {
    const {
      data: { detail },
    } = yield call(createOrder, data);
    yield put(actions.createOrderSuccess());
    yield queryClient.invalidateQueries('partner-balance');
    yield put(addSuccessMessage(detail));
  } catch (error) {
    const errorMessage = ErrorUtils.convertError(error);
    yield put(actions.createOrderFailed(errorMessage));
    yield put(
      addErrorMessage(typeof errorMessage === 'string' ? errorMessage : t('createOrderFailed', { ns: 'dashboard' }))
    );
  }
}

function* createBulkOrderSaga(action: ReturnType<typeof actions.createBulkOrderRequest>) {
  const data = action.payload;

  try {
    const {
      data: { detail },
    } = yield call(createBulkOrder, data);
    yield put(actions.createBulkOrderSuccess());
    yield queryClient.invalidateQueries('partner-balance');

    yield put(addSuccessMessage(detail || 'success'));
  } catch (error) {
    const errorMessage = ErrorUtils.accumulateErrorMessage(error);
    yield put(actions.createBulkOrderFailed(errorMessage));
    yield put(addErrorMessage(errorMessage));
  }
}

function* bulkEmailValidationSaga(action: ReturnType<typeof actions.bulkEmailValidationRequest>) {
  const formData = action.payload;

  try {
    const { data }: SagaReturnType<typeof bulkEmailsVerification> = yield call(bulkEmailsVerification, formData);
    yield put(actions.bulkEmailValidationSuccess(data));
    const deliverableEmails = data.filter(({ result }) => ['deliverable', 'catch_all', 'unknown'].includes(result));
    deliverableEmails.forEach(({ address }, index) => {
      formData.append(`validated_emails[${index}]`, address);
    });
    if (_.isEmpty(deliverableEmails)) {
      const errorMessage = t('errorEmailsDidNotPassValidation', { ns: 'dashboard' });
      yield put(actions.bulkEmailValidationFailed(errorMessage));
      yield put(addErrorMessage(errorMessage));
      return;
    }

    yield put(actions.uploadBulkOrderRequest(formData));
  } catch (error) {
    const errorMessage = ErrorUtils.accumulateErrorMessage(error) || error.response.data?.[0];
    yield put(actions.bulkEmailValidationFailed(errorMessage));
    yield put(addErrorMessage(errorMessage));
  }
}

function* uploadBulkOrderSaga(action: ReturnType<typeof actions.uploadBulkOrderRequest>) {
  const formData = action.payload;

  try {
    const { data } = (yield call(uploadBulkOrder, formData)) as AxiosResponse<SendBulkOrder>;

    if (data.orders?.length === 0) {
      const errorMessage = t('uploadedFileHasNoOrders', { ns: 'dashboard' });
      yield put(actions.uploadBulkOrderFailed(errorMessage));
      yield put(addErrorMessage(errorMessage));
    } else {
      yield put(actions.uploadBulkOrderSuccess(data));
      yield queryClient.invalidateQueries('partner-balance');
      yield put(addSuccessMessage(t('fileSuccessfullyUploaded', { ns: 'dashboard' })));
    }
  } catch (error) {
    const errorMessage = ErrorUtils.accumulateErrorMessage(error);
    yield put(actions.uploadBulkOrderFailed(errorMessage));
    yield put(addErrorMessage(errorMessage));
  }
}

function* dashboardSaga() {
  yield takeEvery(types.REQUEST_LINEAR_GRAPH_DATA, getLinearGraphItemsSaga);
  yield takeEvery(types.SET_LINEAR_GRAPH_MODE, getLinearGraphItemsSaga);
  yield takeEvery(types.SHIFT_LINEAR_GRAPH, getLinearGraphItemsSaga);
  yield takeLatest(types.REQUEST_SUMMARY_GRAPH_DATA, getOrderSummarySaga);
  yield takeLatest(types.SET_SUMMARY_GRAPH_MODE, getOrderSummarySaga);
  yield takeLatest(types.SET_SUMMARY_GRAPH_START_DATE, getOrderSummarySaga);
  yield takeLatest(types.CREATE_ORDER_REQUEST, createOrderSaga);
  yield takeLatest(types.CREATE_BULK_ORDER_REQUEST, createBulkOrderSaga);
  yield takeLatest(types.BULK_EMAIL_VALIDATION_REQUEST, bulkEmailValidationSaga);
  yield takeLatest(types.UPLOAD_BULK_ORDER_REQUEST, uploadBulkOrderSaga);
}

export default dashboardSaga;
